/**
 * Recursively search through tree structure and filter items based on search term
 * Maintains tree structure by keeping parent nodes if children match
 */

export interface Course {
  id: number | string;
  shortname?: string;
  fullname?: string;
  name: string;
  hidden?: boolean;
  groupId?: string;
  isCustom?: boolean;
  isGroup?: boolean;
}

export interface HasCourses {
  courses?: Course[];
  customCourses?: Course[];
  groups?: Course[];
}

export interface Aircraft extends HasCourses {
  id: number;
  name: string;
  types?: Aircraft[];
  _matchesSearch?: boolean;
}

export interface Operation extends HasCourses {
  id: number;
  name: string;
  aircraft?: Aircraft[];
  _matchesSearch?: boolean;
}

export interface Position extends HasCourses {
  id: number;
  name: string;
  roleId: number;
  operations?: Operation[];
  aircraft?: Aircraft[];
  _matchesSearch?: boolean;
}

export interface Role extends HasCourses {
  id: number;
  name: string;
  aircraft?: Aircraft[];
  positions?: Position[];
  _matchesSearch?: boolean;
}

const searchInText = (text: string | undefined, searchTerm: string): boolean => {
  if (!text || !searchTerm) return false;
  return text.toLowerCase().includes(searchTerm.toLowerCase());
};

const searchInCourse = (course: Course | undefined, searchTerm: string): boolean => {
  if (!course) return false;

  // Convert ID to string for searching (handle both id and groupId for groups)
  const idString = (course.id || course.groupId)?.toString() || "";

  return (
    searchInText(idString, searchTerm) ||
    searchInText(course.shortname, searchTerm) ||
    searchInText(course.fullname, searchTerm) ||
    searchInText(course.name, searchTerm)
  );
};

// Helper to get the appropriate ID and prefix for a course
const getCourseIdWithPrefix = (course: Course, type: "course" | "custom" | "group"): string => {
  if (type === "group") {
    const id = course.groupId || course.id;
    return id ? `g_${id}` : "";
  }
  if (type === "custom") {
    return course.id ? `cc_${course.id}` : "";
  }
  return course.id?.toString() || "";
};

// Helper to check if a course matches (including prefixed ID search)
const courseMatches = (course: Course, searchTerm: string, type: "course" | "custom" | "group" = "course"): boolean => {
  const prefixedId = getCourseIdWithPrefix(course, type);
  return searchInCourse(course, searchTerm) || (type !== "course" && searchInText(prefixedId, searchTerm));
};

// Helper to check if any courses match in an object with courses
const anyCoursesMatch = (item: HasCourses, searchTerm: string): boolean => {
  return !!(
    item.courses?.some((c) => courseMatches(c, searchTerm, "course")) ||
    item.customCourses?.some((c) => courseMatches(c, searchTerm, "custom")) ||
    item.groups?.some((g) => courseMatches(g, searchTerm, "group"))
  );
};

const filterCourses = (courses: Course[] | undefined, searchTerm: string, prefix?: string): Course[] | undefined => {
  if (!courses || !searchTerm) return courses;
  const type = prefix === "cc_" ? "custom" : prefix === "g_" ? "group" : "course";
  return courses.filter((course) => courseMatches(course, searchTerm, type));
};

const filterAircraft = (aircraftList: Aircraft[] | undefined, searchTerm: string): Aircraft[] => {
  if (!aircraftList || !searchTerm) return aircraftList || [];

  return aircraftList
    .map((aircraft): Aircraft | null => {
      const matchesAircraft = searchInText(aircraft.name, searchTerm);

      // If aircraft itself matches, show all its courses and nested types
      if (matchesAircraft) {
        return {
          ...aircraft,
          _matchesSearch: true,
        };
      }

      // Filter direct courses
      const filteredCourses = filterCourses(aircraft.courses, searchTerm);
      const filteredCustomCourses = filterCourses(aircraft.customCourses, searchTerm, "cc_");
      const filteredGroups = filterCourses(aircraft.groups, searchTerm, "g_");

      // Recurse into nested type nodes (designator → types[])
      const filteredTypes = filterAircraft(aircraft.types, searchTerm);

      const hasMatchingChildren =
        (filteredCourses?.length || 0) > 0 ||
        (filteredCustomCourses?.length || 0) > 0 ||
        (filteredGroups?.length || 0) > 0 ||
        (filteredTypes?.length || 0) > 0;

      if (!hasMatchingChildren) return null;

      return {
        ...aircraft,
        courses: filteredCourses,
        customCourses: filteredCustomCourses,
        groups: filteredGroups,
        types: filteredTypes,
        _matchesSearch: false,
      };
    })
    .filter((item): item is Aircraft => item !== null);
};

const filterOperations = (operations: Operation[] | undefined, searchTerm: string): Operation[] => {
  if (!operations || !searchTerm) return operations || [];

  return operations
    .map((operation): Operation | null => {
      const matchesOperation = searchInText(operation.name, searchTerm);
      
      // If operation itself matches, show all its content
      if (matchesOperation) {
        return {
          ...operation,
          _matchesSearch: true,
        };
      }
      
      // Otherwise, filter children
      const filteredCourses = filterCourses(operation.courses, searchTerm);
      const filteredCustomCourses = filterCourses(operation.customCourses, searchTerm, "cc_");
      const filteredGroups = filterCourses(operation.groups, searchTerm, "g_");
      const filteredAircraft = filterAircraft(operation.aircraft, searchTerm);

      const hasMatchingChildren =
        (filteredCourses?.length || 0) > 0 ||
        (filteredCustomCourses?.length || 0) > 0 ||
        (filteredGroups?.length || 0) > 0 ||
        filteredAircraft.length > 0;

      if (!hasMatchingChildren) return null;

      return {
        ...operation,
        courses: filteredCourses,
        customCourses: filteredCustomCourses,
        groups: filteredGroups,
        aircraft: filteredAircraft,
        _matchesSearch: false,
      };
    })
    .filter((item): item is Operation => item !== null);
};

const filterPositions = (positions: Position[] | undefined, searchTerm: string): Position[] => {
  if (!positions || !searchTerm) return positions || [];

  return positions
    .map((position): Position | null => {
      const matchesPosition = searchInText(position.name, searchTerm);
      
      // If position itself matches, show all its content
      if (matchesPosition) {
        return {
          ...position,
          _matchesSearch: true,
        };
      }
      
      // Otherwise, filter children
      const filteredCourses = filterCourses(position.courses, searchTerm);
      const filteredCustomCourses = filterCourses(position.customCourses, searchTerm, "cc_");
      const filteredGroups = filterCourses(position.groups, searchTerm, "g_");
      const filteredOperations = filterOperations(position.operations, searchTerm);
      const filteredAircraft = filterAircraft(position.aircraft, searchTerm);

      const hasMatchingChildren =
        (filteredCourses?.length || 0) > 0 ||
        (filteredCustomCourses?.length || 0) > 0 ||
        (filteredGroups?.length || 0) > 0 ||
        filteredOperations.length > 0 ||
        filteredAircraft.length > 0;

      if (!hasMatchingChildren) return null;

      return {
        ...position,
        courses: filteredCourses,
        customCourses: filteredCustomCourses,
        groups: filteredGroups,
        operations: filteredOperations,
        aircraft: filteredAircraft,
        _matchesSearch: false,
      };
    })
    .filter((item): item is Position => item !== null);
};

export const filterRelations = (roles: Role[], searchTerm: string): Role[] => {
  if (!searchTerm || searchTerm.trim() === "") {
    // Return original data without _matchesSearch flags
    return roles;
  }

  const trimmedSearchTerm = searchTerm.trim();

  return roles
    .map((role): Role | null => {
      const matchesRole = searchInText(role.name, trimmedSearchTerm);
      
      // If role itself matches, show all its content
      if (matchesRole) {
        return {
          ...role,
          _matchesSearch: true,
        };
      }
      
      // Otherwise, filter children
      const filteredCourses = filterCourses(role.courses, trimmedSearchTerm);
      const filteredCustomCourses = filterCourses(role.customCourses, trimmedSearchTerm, "cc_");
      const filteredGroups = filterCourses(role.groups, trimmedSearchTerm, "g_");
      const filteredAircraft = filterAircraft(role.aircraft, trimmedSearchTerm);
      const filteredPositions = filterPositions(role.positions, trimmedSearchTerm);

      const hasMatchingChildren =
        (filteredCourses?.length || 0) > 0 ||
        (filteredCustomCourses?.length || 0) > 0 ||
        (filteredGroups?.length || 0) > 0 ||
        filteredAircraft.length > 0 ||
        filteredPositions.length > 0;

      if (!hasMatchingChildren) return null;

      return {
        ...role,
        courses: filteredCourses,
        customCourses: filteredCustomCourses,
        groups: filteredGroups,
        aircraft: filteredAircraft,
        positions: filteredPositions,
        _matchesSearch: false,
      };
    })
    .filter((item): item is Role => item !== null);
};

/**
 * Get all node IDs that should be expanded to show search results
 */
export const getExpandedNodeIds = (roles: Role[], searchTerm: string): Set<string> => {
  if (!searchTerm || searchTerm.trim() === "") return new Set();

  const expandedIds = new Set<string>();
  const trimmedSearchTerm = searchTerm.trim();

  roles.forEach((role) => {
    const roleKey = `/role${role.id}`;
    let shouldExpandRole = false;

    // Check direct courses under role
    if (anyCoursesMatch(role, trimmedSearchTerm)) {
      shouldExpandRole = true;
    }

    // Check aircraft under role
    role.aircraft?.forEach((aircraft) => {
      const aircraftKey = `${roleKey}/a${aircraft.id}`;
      const typeMatches = aircraft.types?.some(
        (t) => searchInText(t.name, trimmedSearchTerm) || anyCoursesMatch(t, trimmedSearchTerm),
      );
      if (searchInText(aircraft.name, trimmedSearchTerm) || anyCoursesMatch(aircraft, trimmedSearchTerm) || typeMatches) {
        shouldExpandRole = true;
        expandedIds.add(aircraftKey);
      }
      aircraft.types?.forEach((type) => {
        const typeKey = `${aircraftKey}/a${type.id}`;
        if (searchInText(type.name, trimmedSearchTerm) || anyCoursesMatch(type, trimmedSearchTerm)) {
          expandedIds.add(typeKey);
        }
      });
    });

    // Check positions
    role.positions?.forEach((position) => {
      const positionKey = `${roleKey}/p${position.id}`;
      let shouldExpandPosition = false;

      if (searchInText(position.name, trimmedSearchTerm) || anyCoursesMatch(position, trimmedSearchTerm)) {
        shouldExpandRole = true;
        shouldExpandPosition = true;
      }

      // Check aircraft under position
      position.aircraft?.forEach((aircraft) => {
        const aircraftKey = `${positionKey}/a${aircraft.id}`;
        const typeMatches = aircraft.types?.some(
          (t) => searchInText(t.name, trimmedSearchTerm) || anyCoursesMatch(t, trimmedSearchTerm),
        );
        if (searchInText(aircraft.name, trimmedSearchTerm) || anyCoursesMatch(aircraft, trimmedSearchTerm) || typeMatches) {
          shouldExpandRole = true;
          shouldExpandPosition = true;
          expandedIds.add(aircraftKey);
        }
        aircraft.types?.forEach((type) => {
          const typeKey = `${aircraftKey}/a${type.id}`;
          if (searchInText(type.name, trimmedSearchTerm) || anyCoursesMatch(type, trimmedSearchTerm)) {
            expandedIds.add(typeKey);
          }
        });
      });

      // Check operations under position
      position.operations?.forEach((operation) => {
        const operationKey = `${positionKey}/o${operation.id}`;
        let shouldExpandOperation = false;

        if (searchInText(operation.name, trimmedSearchTerm) || anyCoursesMatch(operation, trimmedSearchTerm)) {
          shouldExpandRole = true;
          shouldExpandPosition = true;
          shouldExpandOperation = true;
        }

        // Check aircraft under operation
        operation.aircraft?.forEach((aircraft) => {
          const aircraftKey = `${operationKey}/a${aircraft.id}`;
          const typeMatches = aircraft.types?.some(
            (t) => searchInText(t.name, trimmedSearchTerm) || anyCoursesMatch(t, trimmedSearchTerm),
          );
          if (searchInText(aircraft.name, trimmedSearchTerm) || anyCoursesMatch(aircraft, trimmedSearchTerm) || typeMatches) {
            shouldExpandRole = true;
            shouldExpandPosition = true;
            shouldExpandOperation = true;
            expandedIds.add(aircraftKey);
          }
          aircraft.types?.forEach((type) => {
            const typeKey = `${aircraftKey}/a${type.id}`;
            if (searchInText(type.name, trimmedSearchTerm) || anyCoursesMatch(type, trimmedSearchTerm)) {
              expandedIds.add(typeKey);
            }
          });
        });

        if (shouldExpandOperation) {
          expandedIds.add(operationKey);
        }
      });

      if (shouldExpandPosition) {
        expandedIds.add(positionKey);
      }
    });

    if (shouldExpandRole) {
      expandedIds.add(roleKey);
    }
  });

  return expandedIds;
};
